/**
 * CreditControlPage — `/credit-control`, the list of billing accounts
 * (docs/features/credit-control/06_ui_strategy.md §2): which accounts are on
 * stop, which payment methods each payer has, or waiting on credit
 * decisions, with Import accounts… for loading today's payment settings.
 *
 * Search, the filters, sort and paging all run on the server (docs/list-
 * endpoints.md); the page only holds the controls. Customers on the default
 * (card only, no billing account) are not listed. A row opens the Settings of
 * the customer it was created from. Managers and admins import; everyone else
 * reads.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  BillingAccountListOptions,
  ListResponse,
} from "@shared-core/data/adapter";
import type {
  BillingAccountListItem,
  PaymentMethod,
  BillingLedger,
} from "@shared-core/data/credit-control-types";
import { creditListStatus } from "@shared-core/domain/credit-control-presentation";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { useCanManageCreditControl } from "../app/auth/hooks";
import { ImportCreditAccountsDialog } from "../app/components/domain/credit-control/ImportCreditAccountsDialog";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Checkbox } from "../app/components/primitives/checkbox";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

const PAGE_SIZE = 25;

type LedgerFilter = "all" | BillingLedger;
type MethodFilter = "all" | PaymentMethod;

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; list: ListResponse<BillingAccountListItem> };

export function CreditControlPage() {
  const adapter = useDataAdapter();
  const canManage = useCanManageCreditControl();
  const [search, setSearch] = useState("");
  const [ledger, setLedger] = useState<LedgerFilter>("all");
  const [method, setMethod] = useState<MethodFilter>("all");
  const [onStop, setOnStop] = useState(false);
  const [ordersWaiting, setOrdersWaiting] = useState(false);
  const [page, setPage] = useState(1);
  const [reloads, setReloads] = useState(0);
  const [importing, setImporting] = useState(false);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const debouncedSearch = useDebouncedValue(search, 300);

  // Changing any filter starts again at the first page.
  const resetPage = useCallback(() => setPage(1), []);

  useEffect(() => {
    let cancelled = false;
    const options: BillingAccountListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort: "name",
      q: debouncedSearch.trim() || undefined,
      ledger: ledger === "all" ? undefined : ledger,
      method: method === "all" ? undefined : method,
      onStop: onStop ? true : undefined,
      hasPendingHolds: ordersWaiting ? true : undefined,
    };
    adapter
      .listBillingAccounts(options)
      .then((list) => {
        if (!cancelled) setState({ kind: "loaded", list });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error
                ? err.message
                : "Could not load the billing accounts.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    adapter,
    page,
    debouncedSearch,
    ledger,
    method,
    onStop,
    ordersWaiting,
    reloads,
  ]);

  const filtered =
    debouncedSearch.trim() !== "" ||
    ledger !== "all" ||
    method !== "all" ||
    onStop ||
    ordersWaiting;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Credit control
          </h1>
          <p className="text-sm text-muted-foreground">
            Billing accounts: how each payer pays, on what credit terms, and who
            is on stop.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setImporting(true)}>Import accounts…</Button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label htmlFor="credit-search">Search</Label>
          <Input
            id="credit-search"
            type="search"
            className="w-64"
            placeholder="Search name or code"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="credit-ledger">Sage ledger</Label>
          <Select
            value={ledger}
            onValueChange={(v) => {
              setLedger(v as LedgerFilter);
              resetPage();
            }}
          >
            <SelectTrigger id="credit-ledger" className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="POL">POL</SelectItem>
              <SelectItem value="BCQ">BCQ</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="credit-method">Method</Label>
          <Select
            value={method}
            onValueChange={(v) => {
              setMethod(v as MethodFilter);
              resetPage();
            }}
          >
            <SelectTrigger id="credit-method" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="proforma">Pro-forma</SelectItem>
              <SelectItem value="credit_account">Credit account</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Checkbox
            id="credit-on-stop"
            checked={onStop}
            onCheckedChange={(checked) => {
              setOnStop(checked === true);
              resetPage();
            }}
          />
          <Label htmlFor="credit-on-stop" className="font-normal">
            On stop
          </Label>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Checkbox
            id="credit-orders-waiting"
            checked={ordersWaiting}
            onCheckedChange={(checked) => {
              setOrdersWaiting(checked === true);
              resetPage();
            }}
          />
          <Label htmlFor="credit-orders-waiting" className="font-normal">
            Orders waiting
          </Label>
        </div>
      </div>

      {state.kind === "failed" && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load the billing accounts: {state.message}
          </AlertDescription>
        </Alert>
      )}
      {state.kind === "loading" && (
        <div className="space-y-2" aria-label="Loading billing accounts">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      )}
      {state.kind === "loaded" && state.list.items.length === 0 && (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
          {filtered
            ? "No billing accounts match these filters."
            : "No billing accounts yet. Import accounts to load today's credit accounts."}
        </p>
      )}
      {state.kind === "loaded" && state.list.items.length > 0 && (
        <>
          <div className="rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Sage account</TableHead>
                  <TableHead>Methods</TableHead>
                  <TableHead>Terms</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Waiting</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.list.items.map((account) => (
                  <AccountRow key={account.ref} account={account} />
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            {state.list.total}{" "}
            {state.list.total === 1 ? "billing account" : "billing accounts"} ·{" "}
            {PAGE_SIZE} per page. A row opens its customer's Settings.
          </p>
          <ListPagination
            page={state.list.page}
            totalPages={state.list.totalPages}
            onPageChange={setPage}
          />
        </>
      )}

      <ImportCreditAccountsDialog
        open={importing}
        onOpenChange={setImporting}
        onImported={() => {
          setPage(1);
          setReloads((n) => n + 1);
        }}
      />
    </div>
  );
}

function AccountRow({ account }: { account: BillingAccountListItem }) {
  const status = creditListStatus(account);
  const to = account.customerId
    ? paths.customerSettings({ customerId: account.customerId })
    : null;
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        {to ? (
          <Link to={to} className="text-primary hover:underline">
            {account.customerCode}
          </Link>
        ) : (
          account.customerCode
        )}
      </TableCell>
      <TableCell>
        {to ? (
          <Link to={to} className="hover:underline">
            {account.name}
          </Link>
        ) : (
          account.name
        )}
      </TableCell>
      <TableCell className="font-mono text-xs">
        {account.sageAccountRef ?? ""}
      </TableCell>
      <TableCell>{account.methodsLabel}</TableCell>
      <TableCell>{account.termsLabel ?? ""}</TableCell>
      <TableCell>
        {status && (
          <Badge variant={status.variant === "on_stop" ? "warning" : "neutral"}>
            {status.label}
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {account.pendingHoldCount}
      </TableCell>
    </TableRow>
  );
}
